import { createFileRoute, Link } from "@tanstack/react-router";
import { breadcrumbSchema } from "@/lib/schema";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { InternalLinkBlock } from "@/components/InternalLinkBlock";
import { PAL_COUNT } from "@/lib/seo-snapshots";
import { ContextVisualStrip } from "@/components/ContextVisualStrip";

/**
 * Wiki directory hub — owns the "palworld wiki" / "pal world wiki" query.
 *
 * Deliberately NOT a copy of any other page: it holds no Pal grid, no map, no
 * calculator. It is a table-of-contents that explains what each database
 * section answers and links straight to it, so it competes for the generic
 * "wiki" intent while every tool page keeps its own exact-match head term.
 */
const FAQ: FaqItem[] = [
  {
    q: "What is the Pal Stars Palworld wiki?",
    a: `Pal Stars is an unofficial Palworld wiki covering ${PAL_COUNT} Pals, items and weapons, passive skills, the technology tree, breeding, mounts, elements, boss locations, an interactive Palpagos map and the Palworld Official Card Game. Every section is a live database page rather than a static article.`,
  },
  {
    q: "Where do I start in the Palworld wiki?",
    a: "Start with the Pals database if you want stats and card data, the breeding calculator if you are chasing a specific Pal, or the interactive map if you are looking for spawns, alphas, dungeons and fast travel points. If you are new to the game, read the beginners wiki article first.",
  },
  {
    q: "Is this the official Palworld wiki?",
    a: "No. Pal Stars is a fan-run Palworld wiki. Palworld is developed and published by Pocketpair and the Palworld Official Card Game is published by Bushiroad; all game names and card images remain their property.",
  },
  {
    q: "How often is the wiki updated?",
    a: "Pal data, guides and the technology tree are reviewed against each Palworld patch, and Palworld TCG card prices refresh from live marketplace data every day.",
  },
  {
    q: "Does the wiki cover the Palworld card game as well as the game?",
    a: "Yes. Alongside the game database there is a full card list with printed card codes, per-card price history, a deck builder and a market price index for the Palworld Official Card Game.",
  },
];

const SECTIONS: { heading: string; blurb: string; links: { to: string; label: string; note: string }[] }[] = [
  {
    heading: "Pal database",
    blurb: "Stat sheets, elements, work suitability, drops and card data for every Pal.",
    links: [
      { to: "/pals", label: "All Palworld Pals", note: `Searchable list of ${PAL_COUNT} Pals with stats and card numbers.` },
      { to: "/tier-list", label: "Palworld tier list", note: "Every Pal ranked SS to C for combat, work and mounts." },
      { to: "/capture-rates", label: "Palworld capture rates", note: "Sphere odds per Pal by level and HP remaining." },
      { to: "/mounts", label: "Palworld mount speeds", note: "Ground, water and flying mount speed table." },
    ],
  },
  {
    heading: "Calculators",
    blurb: "Answer a specific question with real numbers instead of reading a wall of text.",
    links: [
      { to: "/breeding", label: "Palworld breeding calculator", note: "Parent pairs, reverse lookup and passive inheritance." },
      { to: "/elements", label: "Palworld type chart", note: "Every element matchup with a live multiplier lookup." },
      { to: "/damage", label: "Palworld damage calculator", note: "Model any hit with crit, resistance and type modifiers." },
      { to: "/team-builder", label: "Palworld team builder", note: "Build a squad and check element coverage gaps." },
    ],
  },
  {
    heading: "World & progression",
    blurb: "Where things are and what to unlock next.",
    links: [
      { to: "/map", label: "Palworld interactive map", note: "Regions, alphas, dungeons, resources and fast travel." },
      { to: "/bosses", label: "Palworld boss list", note: "Alpha and tower bosses with weaknesses and drops." },
      { to: "/technology-tree", label: "Palworld technology tree", note: "Every unlock from level 1 to 60 with point costs." },
      { to: "/items", label: "Palworld items and weapons", note: "Recipes, unlock levels and key stats per item." },
      { to: "/passives", label: "Palworld passive skills", note: "Every trait with tier, effect and best use." },
      { to: "/eggs", label: "Palworld eggs", note: "Egg types, hatch times and what can hatch from each." },
    ],
  },
  {
    heading: "Palworld card game",
    blurb: "The trading card side of Palworld, with live market data.",
    links: [
      { to: "/cards", label: "Palworld TCG card list", note: "Every printed card with official scans and codes." },
      { to: "/prices", label: "Palworld card prices", note: "Daily marketplace prices and full price history." },
      { to: "/tcg-price-index", label: "Palworld TCG price index", note: "Whole-market benchmark and trend snapshots." },
      { to: "/palworld-tcg", label: "Palworld TCG answers", note: "Rules, sets, sealed products and rarity explained." },
    ],
  },
  {
    heading: "Guides",
    blurb: "Long-form walkthroughs when the data pages are not enough.",
    links: [
      { to: "/guides", label: "All Palworld guides", note: "Breeding, farming, bosses, maps, TCG strategy and more." },
      { to: "/blog", label: "Palworld news and blog", note: "Patch notes, card set news and market write-ups." },
    ],
  },
];

/** Slim card list for the directory — keeps the full article corpus out of the entry bundle. */
async function topicCards() {
  const { WIKI_PAGES } = await import("@/lib/wiki-pages");
  return WIKI_PAGES.map((p) => ({ slug: p.slug, h1: p.h1, description: p.description }));
}

export const Route = createFileRoute("/palworld-wiki/")({
  loader: async () => ({ pages: await topicCards() }),
  head: ({ loaderData }) => ({
    meta: [
      { title: "Palworld Wiki — Pals, Maps, Breeding & Cards" },
      {
        name: "description",
        content:
          "Unofficial Palworld wiki: Pal stat sheets, breeding calculator, interactive map, type chart, items, passives, technology tree and Palworld card game prices.",
      },
      { property: "og:title", content: "Palworld Wiki — Pal Stars" },
      { property: "og:description", content: "Every Palworld database, calculator, map and card page in one directory." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://palstars.com/palworld-wiki" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Palworld Wiki — Pal Stars" },
      { name: "twitter:description", content: "Every Palworld database, calculator, map and card page in one directory." },
      { name: "robots", content: "index, follow, max-snippet:-1, max-image-preview:large" },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/palworld-wiki" }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["Palworld Wiki", "/palworld-wiki"]]),
      faqSchema(FAQ, "https://palstars.com/palworld-wiki"),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "@id": "https://palstars.com/palworld-wiki#page",
          url: "https://palstars.com/palworld-wiki",
          name: "Palworld Wiki — Pal Stars",
          description:
            "Directory of every Pal Stars Palworld wiki section: Pals, breeding, map, elements, items, passives, technology tree and the Palworld card game.",
          isPartOf: { "@type": "WebSite", name: "Pal Stars", url: "https://palstars.com" },
          hasPart: [
            ...SECTIONS.flatMap((s) =>
              s.links.map((l) => ({ "@type": "WebPage", name: l.label, url: `https://palstars.com${l.to}` })),
            ),
            ...(loaderData?.pages ?? []).map((p) => ({
              "@type": "WebPage",
              name: p.h1,
              url: `https://palstars.com/palworld-wiki/${p.slug}`,
            })),
          ],
        }),
      },
    ],
  }),
  component: WikiPage,
});

function WikiPage() {
  const { pages } = Route.useLoaderData();
  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
      <h1 className="text-3xl sm:text-4xl font-bold">Palworld Wiki</h1>
      <p className="aeo-answer mt-3 max-w-3xl text-muted-foreground">
        This is the Pal Stars Palworld wiki index — an unofficial, fan-run reference to {PAL_COUNT} Pals, items and
        weapons, passive skills, the technology tree, breeding, the Palpagos map, boss locations and the Palworld
        Official Card Game. Pick the section that answers your question below; every link opens a live data page, not a
        static article.
      </p>
      <ContextVisualStrip variant="wiki" />

      <section className="mt-10 glass rounded-2xl p-6">
        <h2 className="text-xl font-bold">Wiki articles</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Written explanations for the parts of Palworld that are not a table: bases, work suitability, sanity, combat,
          multiplayer and regions.
        </p>
        <ul className="mt-4 grid gap-3 sm:grid-cols-2">
          {pages.map((p) => (
            <li key={p.slug} className="rounded-lg border border-border/60 p-3">
              <Link to="/palworld-wiki/$topic" params={{ topic: p.slug }} className="text-sm font-semibold text-primary hover:underline">
                {p.h1}
              </Link>
              <p className="mt-1 text-xs text-muted-foreground">{p.description}</p>
            </li>
          ))}
        </ul>
      </section>

      <div className="mt-10 grid gap-6 lg:grid-cols-2">
        {SECTIONS.map((s) => (
          <section key={s.heading} className="glass rounded-2xl p-6">
            <h2 className="text-xl font-bold">{s.heading}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{s.blurb}</p>
            <ul className="mt-4 space-y-3">
              {s.links.map((l) => (
                <li key={l.to} className="rounded-lg border border-border/60 p-3">
                  <Link to={l.to} className="text-sm font-semibold text-primary hover:underline">
                    {l.label}
                  </Link>
                  <p className="mt-1 text-xs text-muted-foreground">{l.note}</p>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>

      <FaqSection items={FAQ} heading="Palworld wiki FAQ" />
      <InternalLinkBlock path="/palworld-wiki" />
    </div>
  );
}
